export const focusAreas: Record<string, any> = {
  designQuality: {
    title: "Professional Web Design",
    description:
      "Create a stunning, conversion-focused website that captures your brand identity and makes a powerful first impression on Saskatchewan customers and beyond.",
    symptoms: [
      "Website looks outdated or unprofessional compared to competitors",
      "Design doesn't reflect your brand or Saskatchewan business identity",
      "Visitors leave quickly without engaging with your content",
      "Generic template that looks like dozens of other websites",
    ],
    impacts: [
      "Lost credibility and trust with potential customers",
      "Higher bounce rates as visitors don't stay to explore",
      "Missed opportunities to stand out in your local market",
      "Brand perception suffers from poor visual presentation",
    ],
    solutions: [
      "Design custom, modern website that reflects your unique brand story",
      "Implement conversion-optimized layouts that guide visitors to action",
      "Create mobile-first responsive design that looks great on all devices",
      "Showcase your Saskatchewan roots and community connection authentically",
    ],
    nextSteps: [
      {
        step: "Brand Discovery",
        action:
          "Define your unique value proposition and visual identity that resonates with your target audience.",
      },
      {
        step: "Competitive Analysis",
        action:
          "Review competitor websites to identify opportunities to differentiate and stand out.",
      },
      {
        step: "Design Concepts",
        action:
          "Create modern, professional mockups that align with your brand and conversion goals.",
      },
      {
        step: "User Testing",
        action:
          "Validate design with real users to ensure it drives engagement and conversions.",
      },
    ],
    insight:
      "Businesses that invest in professional web design see 200% higher conversion rates and 3x longer average session times compared to generic template websites.",
  },
  functionality: {
    title: "Advanced Website Functionality",
    description:
      "Build powerful website features and capabilities that serve your customers' needs, streamline your operations, and drive business growth through automation.",
    symptoms: [
      "Website only displays information - no interactive features",
      "Missing e-commerce, booking, or other business-critical functions",
      "Manual processes that could be automated through your website",
      "Difficult for customers to take desired actions or contact you",
    ],
    impacts: [
      "Losing sales to competitors with better online capabilities",
      "Time wasted on manual tasks that could be automated",
      "Customer frustration with inability to complete desired actions",
      "Operational inefficiencies from disconnected systems",
    ],
    solutions: [
      "Implement e-commerce solutions with secure payment processing",
      "Build custom booking and scheduling systems tailored to your business",
      "Create customer portals for account management and self-service",
      "Integrate forms, calculators, and tools that generate qualified leads",
    ],
    nextSteps: [
      {
        step: "Define Requirements",
        action:
          "Identify the specific features and functionality your business and customers need most.",
      },
      {
        step: "Technical Planning",
        action:
          "Design the architecture and integrations required to support desired functionality.",
      },
      {
        step: "Phased Development",
        action:
          "Build and launch features incrementally, starting with highest-priority capabilities.",
      },
      {
        step: "User Onboarding",
        action:
          "Train your team and create customer guides to maximize feature adoption and value.",
      },
    ],
    insight:
      "Websites with advanced functionality generate 5x more qualified leads and reduce operational costs by 40% through automation of routine customer interactions.",
  },
  performance: {
    title: "High-Performance Website",
    description:
      "Deliver lightning-fast loading speeds and flawless performance across all devices and network conditions to keep visitors engaged and boost your search rankings.",
    symptoms: [
      "Website takes several seconds to load or times out frequently",
      "Poor mobile performance despite having responsive design",
      "Slow page speed hurting Google search rankings",
      "High bounce rates from visitors leaving before page loads",
    ],
    impacts: [
      "40% of visitors abandon sites that take over 3 seconds to load",
      "Lost search engine visibility due to poor Core Web Vitals scores",
      "Reduced conversions from frustrated visitors leaving early",
      "Negative brand perception from subpar user experience",
    ],
    solutions: [
      "Optimize images, code, and assets for blazing fast load times",
      "Implement modern performance techniques like lazy loading and caching",
      "Use Content Delivery Network (CDN) for global speed optimization",
      "Build with performance-first architecture and clean, efficient code",
    ],
    nextSteps: [
      {
        step: "Performance Audit",
        action:
          "Run comprehensive speed tests to identify specific bottlenecks and optimization opportunities.",
      },
      {
        step: "Technical Optimization",
        action:
          "Implement code splitting, compression, and other performance best practices.",
      },
      {
        step: "Asset Optimization",
        action:
          "Optimize images, videos, and files for web delivery without sacrificing quality.",
      },
      {
        step: "Continuous Monitoring",
        action:
          "Set up performance tracking and alerts to maintain fast speeds as site grows.",
      },
    ],
    insight:
      "A 1-second improvement in page load time can increase conversions by 7% and improve search rankings by up to 10 positions, directly impacting revenue.",
  },
};
